import { NextPage } from 'next';
import Router from 'next/router';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useFirstRender } from 'src/hooks/useFirstReder';
import { createApplicationError } from 'src/utils/errorFabric';

import { BANNER_TYPES } from '@sravni/react-advertisement';
import { Notifications } from '@sravni/react-design-system';
import { Interceptor } from '@sravni/ugc/lib/utils/sentry/Interceptor';

import { LeftColumnContent } from '../../components/LeftColumn';
import { MaterialContent } from '../../components/MaterialContent';
import SeoBlocks from '../../components/SeoBlocks';
import { SpecialOffersIdlePopup } from '../../components/SpecialOffersIdlePopup';
import { SUBDOMAIN_RUBRICS_PARAMS, NEWS_RUBRICS, NEWS_URL_TYPES, URL_TYPES } from '../../constants/news';
import { getMaterialProduct } from '../../helpers/news';
import { makeMaterialMetadata } from '../../helpers/seo';
import { useAdMaterialTags } from '../../hooks/advertising';
import { useOnFingerprintCreate } from '../../hooks/fingerprint';
import { useSingleMaterial } from '../../hooks/news';
import { IRouterQuery } from '../../interfaces/news';
import { fetchAdMaterialTagsAction, fetchBanners } from '../../reducers/advertising';
import { fetchUserLikes } from '../../reducers/initialUserLikes';
import { setMetadata } from '../../reducers/metadata';
import {
  fetchAllDictionariesIfNeeded,
  fetchAllPlacementsShortIfNeeded,
  fetchAuthorsListIfNeeded,
  fetchMaterialSingle,
  fetchPopularMaterialsIfNeeded,
  fetchRubricsListIfNeeded,
  fetchRubricsHasSubDomainListIfNeeded,
} from '../../reducers/news';
import { getWhitelabel, useWhitelabel } from '../../reducers/whitelabel';
import { getAdMaterialTags } from '../../selectors/advertising';
import { getAuthors, getMaterials, getMaterialState, getRubricsHasSubDomain } from '../../selectors/news';
import styles from '../../styles/common.module.scss';
import { App } from '../../types';
import { isServer } from '../../utils/isServer';
import { PageWithSubscriptionPopupProps } from '../_app';

import 'intersection-observer';

interface PageProps {
  error?: {
    statusCode: number;
    statusMessage: string;
  };
}

const MaterialPage: NextPage<App.ReduxNextPageContext<IRouterQuery> & PageWithSubscriptionPopupProps, PageProps> = ({
  subscriptionPopupState,
}) => {
  const adTags = useAdMaterialTags();
  const singleMaterial = useSingleMaterial();
  const productForService = getMaterialProduct({ productTags: singleMaterial?.productTags, adTags });
  const dispatch = useDispatch();
  const isWhitelabel = useWhitelabel();
  const isFirstRender = useFirstRender();

  useOnFingerprintCreate((fingerPrint: string) => {
    dispatch(fetchUserLikes({ fingerPrint }));
  });

  useEffect(() => {
    if (!isWhitelabel && !isFirstRender) {
      dispatch(
        fetchBanners({
          params: {
            BannerTypes: [BANNER_TYPES.OUT_OF_PAGE, BANNER_TYPES.SIDE, BANNER_TYPES.TGBM_7, BANNER_TYPES.TOP],
            Category: 'none',
            Product: productForService,
            Page: 'news',
          },
          source: singleMaterial,
          adTags,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleMaterial?.hideAds, isFirstRender]);

  return (
    <Interceptor componentName="news-material-component">
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.leftColumn}>
            {!isWhitelabel && <LeftColumnContent subscriptionPopupState={subscriptionPopupState} />}
          </div>
          <div className={styles.mainColumn}>
            <Interceptor componentName="news-material-component-MaterialContent">
              <MaterialContent />
              {!isWhitelabel && <SpecialOffersIdlePopup />}
              <Notifications />
            </Interceptor>
          </div>
        </div>
        <SeoBlocks className={styles.content} />
      </div>
    </Interceptor>
  );
};

MaterialPage.getInitialProps = async (ctx: App.ReduxNextPageContext<IRouterQuery>) => {
  const {
    query: { type: queryType, alias, rubricAlias, rubric },
    store: { dispatch, getState },
    asPath,
    res,
  } = ctx;

  const [pathname] = asPath.split('?');

  const isNews = NEWS_URL_TYPES.includes(URL_TYPES[queryType]);

  const isWhitelabel = getWhitelabel(getState());

  if (isNews && NEWS_RUBRICS.includes(rubricAlias)) {
    const [pathname] = asPath.split('?');

    if (res) {
      res.writeHead(301, { Location: pathname });
      res.end();
    } else {
      Router.replace(pathname);
    }

    return {};
  }

  await Promise.all([
    dispatch(fetchAdMaterialTagsAction()),
    dispatch(fetchMaterialSingle({ Alias: alias }, true)),
    dispatch(fetchAllPlacementsShortIfNeeded()),
    dispatch(fetchRubricsListIfNeeded()),
    dispatch(fetchRubricsHasSubDomainListIfNeeded(SUBDOMAIN_RUBRICS_PARAMS)),
    dispatch(fetchAllDictionariesIfNeeded()),
    dispatch(fetchAuthorsListIfNeeded()),
    (!rubricAlias || rubricAlias === 'novosti') &&
      dispatch(
        fetchPopularMaterialsIfNeeded({
          Limit: 10,
        }),
      ),
  ]);

  const materials = getMaterials(getState());
  const materialError = getMaterialState(getState()).error;
  const authorsList = getAuthors(getState());
  const hasSubDomainRubrics = getRubricsHasSubDomain(getState());

  const singleMaterial = materials[0];
  const isMaterialSubDomain = hasSubDomainRubrics.includes(singleMaterial.rubric);

  const author =
    singleMaterial?.authors?.length && authorsList
      ? authorsList?.find((item) => singleMaterial.authors.some((elem) => elem.id === item.id))
      : null;

  const statusCode = 404;
  const statusMessage = 'Not found';

  if (singleMaterial && !isMaterialSubDomain) {
    // Проверка, что queryType соответствует типу материала

    let queryTypeThatMaterialShouldHave = URL_TYPES[singleMaterial.type];

    if (queryType !== queryTypeThatMaterialShouldHave) {
      if (res) res.statusCode = statusCode;

      throw createApplicationError(statusCode, statusMessage);
    }
  } else if (isMaterialSubDomain || (!singleMaterial && materialError)) {
    throw createApplicationError(statusCode, statusMessage);
  }

  const seoData = makeMaterialMetadata(singleMaterial, author, { rubric, rubricAlias });
  dispatch(setMetadata(seoData, pathname));

  const adTags = getAdMaterialTags(getState());
  const productForService = getMaterialProduct({ productTags: singleMaterial?.productTags, adTags });

  if (!isWhitelabel && isServer) {
    await dispatch(
      fetchBanners({
        params: {
          BannerTypes: [BANNER_TYPES.OUT_OF_PAGE, BANNER_TYPES.SIDE, BANNER_TYPES.TGBM_7, BANNER_TYPES.TOP],
          Category: 'none',
          Product: productForService,
          Page: 'news',
        },
        source: singleMaterial,
        adTags,
      }),
    );
  }
};

export default MaterialPage;
